<template>
  <div class="filters">
    <search-panel @input="handleSearchInput" />

    <filter-panel
      class="filters-panel"
      :apply-filter-immediately="false"
      :filter-parameters="formattedFilters"
      @update="applyFilter"
      :notification-text="null"
      :api-request-data="null">

      <!-- refresh/auto-refresh feature -->
      <template #panel-actions>
        <div class="request-controls">
          <b-form-checkbox
            class="checkbox"
            @input="() => {}"
            v-model="autoRefresh"
            :disabled="!!getActiveRequestsCount">
            Auto-refresh
          </b-form-checkbox>
          <b-button :disabled="!!getActiveRequestsCount"
                    @click="forceRefreshFilter"
                    class="button"
                    size="sm"
                    :variant="refreshButtonVariant">
            Refresh
          </b-button>
        </div>
      </template>

    </filter-panel>
  </div>
</template>

<script>
import isEmpty from 'lodash-es/isEmpty';
import moment from 'moment';
import sortBy from 'lodash-es/sortBy';
import cloneDeep from 'lodash-es/cloneDeep';
import getNested from 'lodash-es/get';
import { mapActions, mapGetters } from 'vuex';
import SearchPanel from './SearchPanel';

import FilterPanel, {
  getCurrentFilterValues,
  getCurrentAPIFilterValues,
  applyFilterValues,
  getAPIFilterValues,
} from '../widgets/FilterPanel'

import {
  getCurrentQuarterRange,
  getCurrentYear,
  getToday,
  getYearToDateRange
} from '@/shared/date-utils'

const algoliaBaseParameters = {
  'getRankingInfo': true,
  'analytics': false,
  'enableABTest': false,
  'hitsPerPage': 50,
  'attributesToRetrieve': '*',
  'attributesToSnippet': '*:20',
  'snippetEllipsisText': '…',
  'responseFields': '*',
  'page': 0,
  'facets': ['*'],
};


export default {
  name: 'filters',

  components: {
    SearchPanel,
    FilterPanel,
  },

  props: {
    selectedBerths: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      direction: 'export',

      port: {
        load: null,
        discharge: null,
      },

      filtersReady: false,
      autoRefresh: true,
      updatePending: false,
      defaultFilterValues: {},
      searchIndex: 'polygons_prod',

      filterSchema: {
        loadBerthId: {
          name: 'loadBerthId',
          apiName: 'loadBerthId',
          type: 'multiselect',
          placeholder: 'Load port',
          value: [],
          choices: [],
          options: {
            multiple: true,
            valueLabel: false,
            toggleAll: true,
          },
        },

        dischargeBerthId: {
          name: 'dischargeBerthId',
          apiName: 'dischargeBerthId',
          type: 'multiselect',
          placeholder: 'Discharge port',
          value: [],
          choices: [],
          options: {
            multiple: true,
            valueLabel: false,
            toggleAll: true,
          },
        },

        commodity: {
          name: 'commodity',
          apiName: 'commodity',
          type: 'treeselect',
          placeholder: 'Commodity',
          value: [],
          choices: [],
          rawChoices: [],
          defaultChoice: [],
          options: {
            toggleAll: true,
          },
        },

        dateRange: {
          name: ['startDate', 'endDate'],
          type: 'daterange-picker',
          options: {
            ranges: this.getDatePickerRanges(),
          },

          startDate: {
            name: 'startDate',
            apiName: 'fromDate',
            value: moment('2020-01-01').format('YYYY-MM-DD'),
          },
          endDate: {
            name: 'endDate',
            apiName: 'toDate',
            value: moment().format('YYYY-MM-DD')
          },
        },

        excludeParcel: {
          name: 'excludeParcel',
          apiName: 'parceling',
          type: 'checkbox',
          label: 'Exclude parcel voyages',
          value: false,
          defaultChoice: false,
        },

        subSegment: {
          name: 'subSegment',
          apiName: 'subSegment',
          type: 'treeselect',
          placeholder: 'Segment',
          value: [],
          choices: [],
          rawChoices: [],
          defaultChoice: [],
          options: {
            toggleAll: true,
          },
        },
      },
    };
  },

  methods: {
    ...mapActions('api', ['algoliaSearch']),

    ...mapActions('api/tradeflows', [
      'getCommodities',
      'getSegments',
      'getCountries',
      'getTradeflowsRegionsWithPolygons',
    ]),

    async loadFilters() {
      await this.loadCommodities();
      await this.loadSegments();
      this.filtersReady = true;
    },

    async loadCommodities() {
      this.filterSchema.commodity.choices = await this.getCommodities({group: true});
    },

    async loadSegments() {
      this.filterSchema.subSegment.choices = await this.getSegments({group: true});
    },

    async handleSearchInput(evt) {
      this.port[evt.direction] = evt.value;

      if(getNested(evt, 'value.portId')) {
        await this.loadBerthIdChoices(evt.value.portId, evt.direction);
      }
      this.setBerthIds(evt.value, evt.direction);
    },

    async loadBerthIdChoices(portId, direction) {
      const response = await this.algoliaSearch({
        searchIndex: this.searchIndex,
        query: '',
        searchParameters: {
          ...algoliaBaseParameters,
          filters: `port_id = ${portId} AND entity_type:"BERTH"`,
        },
      });

      const results = getNested(response, 'hits', [])
        .map(item => {
          let coordinates;
          try {
            coordinates = getNested(JSON.parse(item.geom_point), 'coordinates', null)
          } catch(error) {
            coordinates = null;
          }

          return {
            value: item.berth_id,
            text: item.berth_name,
            id: `${item.entity_type}-${item.berth_id}`.toLowerCase(),
            coordinates: coordinates ? [coordinates[1], coordinates[0]] : null,
            radius: item.mbc_radius || null,
          };
      });

      this.filterSchema[`${direction}BerthId`].choices = sortBy(results, item => item.text);
    },

    setBerthIds(port, direction) {
      const targetField = this.filterSchema[`${direction}BerthId`];

      if(!port) {
        targetField.value = [];
        return;
      }

      if(port.entityType === 'PORT') {
        // if values contain port Id, select all berths in this port
        if(isEmpty(targetField.choices) && isEmpty(targetField.value)) {
          // special case: when a port doesn't have any berths
          // it's needed to apply filter anyway with empty berhs and specified ports
          this.applyFilter(getCurrentFilterValues(this.filterSchema));
        }

        targetField.value = cloneDeep(targetField.choices);
      } else {
        // if value is berth, assign it to berthId value as array
        // berths choices are loaded at the moment by .loadBerthIdChoices()
        targetField.value = [
          targetField.choices.find(item => port.berthId === item.value) || {}
        ];
      }
    },

    applyFilter(filterValues) {
      if(!this.filtersReady) {
        return;
      }

      const filterValuesCopy = cloneDeep(filterValues);
      applyFilterValues(this.filterSchema, filterValuesCopy);

      if(this.autoRefresh) {
        let apiFilterValues = getAPIFilterValues(this.filterSchema, filterValuesCopy);
        this.$emit(
          'search',
          {
            ...apiFilterValues,
            loadPortId: getNested(this.port, 'load.portId', null),
            dischargePortId: getNested(this.port, 'discharge.portId', null),
            parceling: apiFilterValues.parceling ? 'exclude' : 'include',
          }
        );
        this.$emit('berths-loaded', this.getBerths());
        this.updatePending = false;
      } else {
        this.updatePending = true;
      }
    },

    forceRefreshFilter() {
      const apiFilterValues = getCurrentAPIFilterValues(this.filterSchema);
      this.$emit(
        'search',
        {
          ...apiFilterValues,
          loadPortId: getNested(this.port, 'load.portId', null),
          dischargePortId: getNested(this.port, 'discharge.portId', null),
          parceling: apiFilterValues.parceling ? 'exclude' : 'include',
        }
      );
      this.$emit('berths-loaded', this.getBerths());
      this.updatePending = false;
    },

    getBerths() {
      let selected;
      let targetField;
      let berths = [];

      for(let direction of ['load', 'discharge']) {
        targetField = this.filterSchema[`${direction}BerthId`];

        const directionBerths = targetField.choices.map(berth => {
          selected = targetField.value.find(
            item => item.value === berth.value
          );
          return { ...berth, selected: !!selected, direction };
        });
        berths = berths.concat(directionBerths);
      }

      return berths;
    },

    getDatePickerRanges() {
      let ranges = {
        'Full period (2015-today)': [
          moment('2015-01-01').toDate(),
          getToday().toDate(),
        ],
        'QTD': getCurrentQuarterRange(),
        'YTD': getYearToDateRange(),
      };

      const currentYear = getCurrentYear();
      for(let year = currentYear - 3; year < currentYear; year++) {
        ranges[`${year}`]  = [
          moment(`${year}-01-01`).toDate(),
          moment(`${year}-12-31`).toDate(),
        ];
      }

      return ranges;
    },

    async applyFilterPreset(preset) {
      let portId;
      portId = getNested(preset, 'params.searchPanel.load.value.portId');
      await this.loadBerthIdChoices(portId, 'load');
      portId = getNested(preset, 'params.searchPanel.discharge.value.portId');
      await this.loadBerthIdChoices(portId, 'discharge');

      applyFilterValues(this.filterSchema, getNested(preset, 'params.filterPanel', {}));
    },

    setDefaultValues() {
      if(!isEmpty(this.defaultFilterValues)) {
        applyFilterValues(this.filterSchema, this.defaultFilterValues);
      }
    },
  },

  computed: {
    ...mapGetters('api', ['getActiveRequestsCount']),
    ...mapGetters('api/presets', ['getActivePreset']),

    refreshButtonVariant() {
      if(!this.autoRefresh && this.updatePending) {
        return 'info';
      } else {
        return 'outline-secondary';
      }
    },

    formattedFilters() {
      return {
        groups: [
          [
            this.filterSchema.loadBerthId,
            this.filterSchema.dischargeBerthId,
          ],
          [
            this.filterSchema.subSegment,
            this.filterSchema.commodity,
            this.filterSchema.dateRange,
          ],
          [
            this.filterSchema.excludeParcel,
          ],
        ]
      };
    },
  },

  watch: {
    selectedBerths: {
      deep: true,
      handler(berthIds) {
        // console.log('selected berths', JSON.stringify(berthIds))

        for(let direction of ['load', 'discharge']) {
          const targetField = this.filterSchema[`${direction}BerthId`];

          targetField.value = berthIds[direction].map(berthId => {
            return targetField.choices.find(
              berth => berth.value === berthId
            );
          });
        }
      },
    },

    autoRefresh(value) {
      if(value && this.updatePending) {
        this.forceRefreshFilter();
      }
    },

    getActivePreset: {
      immediate: true,
      handler: async function (preset) {
        if(preset && this.$route.path !== preset.viewName) {
          return;
        }

        if(!this.filtersReady) {
          await this.loadFilters();
          this.defaultFilterValues = getCurrentFilterValues(this.filterSchema);

          if(!preset) {
            this.setDefaultValues();
          }
        }

        if(preset) {
          await this.applyFilterPreset(preset);
        }
      },
    },
  },
}
</script>

<style scoped lang="scss">
.filters {
  margin-bottom: 1.5rem;
}

.request-controls {
  display: flex;
  flex-direction: column;
  margin: auto 0 5px 0;

  .checkbox {
    white-space: nowrap;
    margin-bottom: 5px;
  }
  .button {
    font-size: 0.85rem;
  }
}
</style>
